import { useContext } from 'react';

// Openlayers
import { Feature as OlFeature } from 'ol';
import { Geometry as OlGeometry } from 'ol/geom';

// Context
import DialogContext from '@/context/DialogContext/DialogContext';

// Custom
import AttachedCommentDialog from '@/views/core/Comments/AttachedCommentDialog';
import { ClosingDetails } from '@/@types/components/authFormController';
import { DCRecord } from '@/@types/lib/dataController';
import DataController from '@/lib/DataController';

import modelComments from '@/models/comments';
import CommentDialog from '@/views/Comments/CommentDialog';
import CommentView from '@/lib/commentViews';

type HandleCloseFn = (result: ClosingDetails) => void;

export interface IComments {
  showCommentsDialog: (gid: string, feature: OlFeature<OlGeometry>, onClose?: HandleCloseFn) => void;
  refreshComments: (gid: string) => Promise<DCRecord[]>;
}

interface IUsecommentProps {
  view?: CommentView;
}

// Hook
function useComments(props?: IUsecommentProps): IComments {
  const { showDialog, hideDialog } = useContext(DialogContext);

  const view = props?.view ? props?.view : CommentView.Manage;
  const isPublic = view === CommentView.Add;

  const dcComments = new DataController(modelComments);

  // const [isLoading, setIsLoading] = useState<boolean>(false);

  const refreshComments = async (gid: string): Promise<DCRecord[]> => {
    // setIsLoading(true);
    try {
      const resp = await dcComments.GetData(`comments/entity/${gid}`);
      if (resp.success) {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data;
        }
        return [];
      }
      // eslint-disable-next-line no-console
      console.error('Error while fetching comments for gid:', gid, 'response:', resp);
      return [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while fetching comments:', error);
      return [];
    } finally {
      // setIsLoading(false);
    }
  };

  const showCommentsDialog = (gid: string, feature: OlFeature<OlGeometry>, onClose?: HandleCloseFn) => {
    if (view === CommentView.Add) {
      showNewCommentDialog(gid, feature, onClose);
    } else {
      // Wait for the initial fetch to complete if it's still in progress
      refreshComments(gid).then((comments) => {
        if (comments.length) {
          showAttachedCommentsDialog(gid, feature, comments, onClose);
        } else {
          showNewCommentDialog(gid, feature, onClose);
        }
      });
    }
  };

  const showNewCommentDialog = (gid: string, feature: OlFeature<OlGeometry>, onClose?: HandleCloseFn) => {
    showDialog(
      CommentDialog,
      {
        dc: dcComments,
        mode: 'insert',
        form: isPublic ? 'insert_public' : 'insert',
        onClose,
        initialRecord: {
          entity_gid: gid,
          geom: feature,
          extent: feature.getGeometry()?.getExtent(),
        },
        level: 2,
        baseRecordPath: isPublic ? `${dcComments.apiPath}/public` : undefined,
      },
      2
    );
  };

  const onAddNewComment = async (result: ClosingDetails) => {
    if (!result) return;
    if (typeof result.success === 'boolean' && !result.success) return;

    if (result.dataChanged) {
      hideDialog(2);
    }
  };

  const showAttachedCommentsDialog = (gid: string, feature: OlFeature<OlGeometry>, comments: DCRecord[], onClose?: HandleCloseFn) => {
    showDialog(
      AttachedCommentDialog,
      {
        dc: dcComments,
        initialRecord: comments,
        onClose,
        gid,
        showNewCommentDialog,
        onAddNewComment,
        feature,
      },
      1
    );
  };

  return {
    showCommentsDialog,
    refreshComments,
  };
}

export default useComments;
